import type { AnalysisSectionSource } from "@/api/analysis-api";

/**
 * Turning a section's stored sources into something readable (SAAS-921).
 *
 * Pure because the interesting parts are all edge cases the model and the
 * ingestion pipeline produce between them — sources with no description, the
 * same file cited twice under different labels, and URLs that need to read as
 * a domain rather than a 200-character tracking link.
 */

export interface DisplaySource {
  /** What the reader sees. */
  label: string;
  /** Where clicking goes, when there is somewhere to go. */
  href?: string;
  /** Drives the icon: a file, a web page, or something the deal record knows. */
  kind: "file" | "web" | "record";
}

/**
 * Sources reach us as stored node JSON, so a field typed as a string is only a
 * string by convention — the pipeline cites a bare page as the number 4, and
 * `(4).trim` is not a function. Coerce what reads as text, ignore the rest.
 */
const asText = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
};

const isHttpUrl = (value: string): boolean =>
  /^https?:\/\//i.test(value.trim());

/** A URL as a person would say it: the domain and the last meaningful segment. */
export const readableUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/\/+$/, "");
    const last = path.split("/").filter(Boolean).pop();
    return last ? `${parsed.hostname}/${last}` : parsed.hostname;
  } catch {
    return url;
  }
};

const kindOf = (source: AnalysisSectionSource): DisplaySource["kind"] => {
  const type = `${source.source_type ?? ""} ${source.file_type ?? ""}`.toLowerCase();
  if (source.file_url) return "file";
  if (type.includes("web") || type.includes("url")) return "web";
  if (isHttpUrl(asText(source.source_description))) return "web";
  return "record";
};

const labelOf = (source: AnalysisSectionSource): string => {
  const description = asText(source.source_description);
  if (description && isHttpUrl(description)) return readableUrl(description);
  if (description) return description;
  // No description is the one case worth naming rather than hiding: the
  // citation is real, we just can't say which page it points at.
  return asText(source.file_type) || "Source";
};

/**
 * Deduplicate and format for display.
 *
 * `attribute_sources` already dedupes server-side, but it keys on the raw
 * description while this keys on what the reader actually sees — two entries
 * that render identically are one citation as far as a person is concerned.
 */
export const displaySources = (
  sources: AnalysisSectionSource[] | undefined
): DisplaySource[] => {
  if (!sources?.length) return [];

  const seen = new Set<string>();
  const result: DisplaySource[] = [];

  for (const source of sources) {
    if (!source) continue;
    const label = labelOf(source);
    const kind = kindOf(source);
    const description = asText(source.source_description);
    const href =
      source.file_url ||
      (isHttpUrl(description) ? description : undefined) ||
      undefined;

    const key = `${kind}::${label.toLowerCase()}::${href ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);

    result.push({ label, href, kind });
  }

  return result;
};
